import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown, 
  Target,
  Award,
  AlertTriangle,
  RefreshCw,
  Calendar,
  DollarSign,
  Percent,
  Activity
} from 'lucide-react';
import axios from 'axios';
import { API, getAuthHeaders } from '../utils/api';

export default function PerformanceDashboard() {
  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);
  const [timeframe, setTimeframe] = useState('all'); // '7d', '30d', '90d', 'all'
  const [performance, setPerformance] = useState({
    total_signals: 0,
    winning_signals: 0,
    losing_signals: 0,
    pending_signals: 0,
    win_rate: null,
    total_pips: 0,
    avg_pips_per_trade: 0,
    best_trade_pips: 0,
    worst_trade_pips: 0,
    profit_factor: null,
    max_consecutive_wins: 0,
    max_consecutive_losses: 0,
    markets_performance: []
  });

  useEffect(() => {
    fetchPerformance();
  }, [timeframe]);

  const fetchPerformance = async () => {
    setLoading(true);
    setNoData(false);
    try {
      // Try to get real performance stats from the API
      const response = await axios.get(`${API}/signals/performance/stats`, {
        withCredentials: true,
        headers: getAuthHeaders(),
        timeout: 10000
      });
      
      if (response.data) {
        const data = response.data;
        // Check if we have any signals with outcomes
        if (data.signals_with_outcome === 0) {
          setNoData(true);
          setPerformance({
            total_signals: data.signals_with_outcome || 0,
            winning_signals: data.tp_hits || 0,
            losing_signals: data.sl_hits || 0,
            pending_signals: 0,
            win_rate: null,
            total_pips: data.total_pips || 0,
            avg_pips_per_trade: data.avg_pips_per_signal || 0,
            best_trade_pips: 0,
            worst_trade_pips: 0,
            profit_factor: null,
            max_consecutive_wins: 0,
            max_consecutive_losses: 0,
            markets_performance: data.performance_by_market || []
          });
        } else {
          setPerformance({
            total_signals: data.signals_with_outcome || 0,
            winning_signals: data.tp_hits || 0,
            losing_signals: data.sl_hits || 0,
            pending_signals: 0,
            win_rate: data.win_rate,
            total_pips: data.total_pips || 0,
            avg_pips_per_trade: data.avg_pips_per_signal || 0,
            best_trade_pips: 0,
            worst_trade_pips: 0,
            profit_factor: null,
            max_consecutive_wins: 0,
            max_consecutive_losses: 0,
            markets_performance: data.performance_by_market || []
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch performance:', error);
      // Show empty state instead of fake data
      setNoData(true);
      setPerformance({
        total_signals: 0,
        winning_signals: 0,
        losing_signals: 0,
        pending_signals: 0,
        win_rate: null,
        total_pips: 0,
        avg_pips_per_trade: 0,
        best_trade_pips: 0,
        worst_trade_pips: 0,
        profit_factor: null,
        max_consecutive_wins: 0,
        max_consecutive_losses: 0,
        markets_performance: []
      });
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, subValue, color, trend }) => (
    <div className={`bg-gradient-to-br from-${color}-500/10 to-transparent border border-${color}-500/20 rounded-xl p-4`}>
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 bg-${color}-500/20 rounded-xl flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${color}-400`} />
        </div>
        {trend !== undefined && (
          <Badge className={`${trend >= 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'} text-xs`}>
            {trend >= 0 ? '+' : ''}{trend}%
          </Badge>
        )}
      </div>
      <p className="text-2xl font-bold text-white mt-3 font-mono">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{label}</p>
      {subValue && <p className="text-xs text-slate-400 mt-0.5">{subValue}</p>}
    </div>
  );

  return (
    <Card className="bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 border-zinc-700 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-zinc-700 bg-gradient-to-r from-zinc-900 to-zinc-800">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 rounded-xl border border-emerald-500/30">
              <BarChart3 className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                Performance Dashboard
                <Badge variant="outline" className="bg-emerald-600/20 border-emerald-500/50 text-emerald-400 text-xs">
                  PREMIUM
                </Badge>
              </h3>
              <p className="text-xs text-slate-400">Track your trading signal performance</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Timeframe Filter */}
            <div className="flex gap-1 bg-zinc-800/50 rounded-lg p-1">
              {[
                { value: '7d', label: '7D' },
                { value: '30d', label: '30D' },
                { value: '90d', label: '90D' },
                { value: 'all', label: 'All' }
              ].map((tf) => (
                <Button
                  key={tf.value}
                  variant="ghost"
                  size="sm"
                  onClick={() => setTimeframe(tf.value)}
                  className={`text-xs px-3 py-1 h-7 ${
                    timeframe === tf.value
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'text-slate-500 hover:text-white'
                  }`}
                  data-testid={`timeframe-${tf.value}`}
                >
                  {tf.label}
                </Button>
              ))}
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchPerformance}
              className="text-slate-400 hover:text-white"
              data-testid="refresh-performance-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="p-4">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="animate-pulse bg-zinc-800/50 rounded-xl h-28" />
            ))}
          </div>
        ) : noData ? (
          /* No Data State */
          <div className="text-center py-12">
            <BarChart3 className="w-16 h-16 mx-auto mb-4 text-slate-600" />
            <h4 className="text-lg font-semibold text-white mb-2">Performance Tracking</h4>
            <p className="text-slate-400 text-sm max-w-md mx-auto mb-4">
              Win rate and performance statistics will appear here as signals are completed 
              and their outcomes (TP hit or SL hit) are recorded.
            </p>
            <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/30">
              Awaiting Signal Outcomes
            </Badge>
          </div>
        ) : (
          <>
            {/* Main Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <StatCard
                icon={Target}
                label="Win Rate"
                value={performance.win_rate !== null ? `${performance.win_rate.toFixed(1)}%` : 'Calculating...'}
                color="emerald"
              />
              <StatCard
                icon={DollarSign}
                label="Total Pips"
                value={`${performance.total_pips >= 0 ? '+' : ''}${performance.total_pips || 0}`}
                color={performance.total_pips >= 0 ? 'emerald' : 'red'}
              />
              <StatCard
                icon={Activity}
                label="Total Signals"
                value={performance.total_signals || 0}
                subValue={`${performance.winning_signals || 0}W / ${performance.losing_signals || 0}L`}
                color="blue"
              />
              <StatCard
                icon={Percent}
                label="Profit Factor"
                value={performance.profit_factor !== null ? performance.profit_factor.toFixed(2) : 'N/A'}
                color="purple"
              />
            </div>

            {/* Secondary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatCard
                icon={TrendingUp}
                label="Best Trade"
                value={performance.best_trade_pips ? `+${performance.best_trade_pips} pips` : 'N/A'}
                color="emerald"
              />
              <StatCard
                icon={TrendingDown}
                label="Worst Trade"
                value={performance.worst_trade_pips ? `${performance.worst_trade_pips} pips` : 'N/A'}
                color="red"
              />
              <StatCard
                icon={Award}
                label="Max Win Streak"
                value={performance.max_consecutive_wins || 'N/A'}
                color="amber"
              />
              <StatCard
                icon={AlertTriangle}
                label="Max Loss Streak"
                value={performance.max_consecutive_losses || 'N/A'}
                color="slate"
              />
            </div>

            {/* Market Performance Table */}
            <div className="bg-zinc-800/30 rounded-xl border border-zinc-700/50 overflow-hidden">
              <div className="p-3 border-b border-zinc-700/50">
                <h4 className="text-sm font-semibold text-white">Performance by Market</h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 text-xs border-b border-zinc-700/50">
                      <th className="p-3">Market</th>
                      <th className="p-3 text-center">Signals</th>
                      <th className="p-3 text-center">Wins</th>
                      <th className="p-3 text-center">Win Rate</th>
                      <th className="p-3 text-right">Pips</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(performance.markets_performance || []).map((market, idx) => (
                      <tr key={market.market} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
                        <td className="p-3">
                          <span className="font-mono font-semibold text-white">{market.market}</span>
                        </td>
                        <td className="p-3 text-center text-slate-400">{market.signals}</td>
                        <td className="p-3 text-center text-emerald-400">{market.wins}</td>
                        <td className="p-3 text-center">
                          <Badge className={`${market.win_rate >= 70 ? 'bg-emerald-500/20 text-emerald-400' : market.win_rate >= 50 ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'}`}>
                            {market.win_rate?.toFixed(1)}%
                          </Badge>
                        </td>
                        <td className={`p-3 text-right font-mono font-semibold ${market.pips >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {market.pips >= 0 ? '+' : ''}{market.pips}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-zinc-800 bg-zinc-900/50">
        <p className="text-[10px] text-slate-500 text-center">
          Performance data is based on closed signals with verified TP/SL hits
        </p>
      </div>
    </Card>
  );
}
